import { useScienceTutorSection } from './scienceTutorContext';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';

import { MoleculeDiagram, ShapeSketchView } from './IonsBondingDiagrams';
import {
  POLARITY_LABELS,
  SHAPE_ORDER,
  VSEPR_SPECIES_CASES,
  analyseVsepr,
  bondPolaritySteps,
  bondsOf,
  checkVsepr,
  type Polarity,
  type ShapeName,
  type VseprAnswers,
  type VseprFieldResult,
} from './ionsBonding';
import { Feedback, Panel } from './IonsBondingShared';
import { pillClass, primaryButtonClass } from './IonsBondingStyles';

const DOMAIN_OPTIONS = [2, 3, 4, 5];
const LONE_PAIR_OPTIONS = [0, 1, 2, 3];
const POLARITY_OPTIONS: Polarity[] = ['polar', 'non-polar', 'ion'];
const MAX_VALENCE_INPUT = 60;

const FIELD_LABELS: Record<keyof VseprAnswers, string> = {
  valenceElectrons: 'Valence electrons',
  domains: 'Electron domains on the central atom',
  lonePairs: 'Lone pairs on the central atom',
  shape: 'Shape',
  polarity: 'Polarity',
};

const EMPTY_ANSWERS: VseprAnswers = {
  valenceElectrons: null,
  domains: null,
  lonePairs: null,
  shape: null,
  polarity: null,
};

const selectClass =
  'mt-2 block w-full rounded-2xl border border-hairline bg-white px-4 py-3 text-sm font-bold text-ink';

export function SeniorMode() {
  const [speciesId, setSpeciesId] = useState(VSEPR_SPECIES_CASES[0].id);
  const [answers, setAnswers] = useState<VseprAnswers>(EMPTY_ANSWERS);
  useScienceTutorSection('SeniorMode', { speciesId, answers });
  const [results, setResults] = useState<VseprFieldResult[] | null>(null);
  const species = VSEPR_SPECIES_CASES.find((entry) => entry.id === speciesId) ?? VSEPR_SPECIES_CASES[0];
  const model = analyseVsepr(species);
  const polaritySteps = bondPolaritySteps(species.structure);
  const central = species.structure.atoms[species.centralIndex];
  const terminalSymbols = bondsOf(species.structure, species.centralIndex).map(
    (bond) => species.structure.atoms[bond.from === species.centralIndex ? bond.to : bond.from].element,
  );
  const complete = Object.values(answers).every((value) => value !== null);

  const update = <K extends keyof VseprAnswers>(key: K, value: VseprAnswers[K]) => {
    setAnswers((current) => ({ ...current, [key]: value }));
    setResults(null);
  };

  return (
    <Panel
      eyebrow="Year 11–12 · Lewis structures and VSEPR"
      title="Predict the shape from electron domains"
      description="Count valence electrons, find the domains and lone pairs on the central atom, then name the shape and decide whether the species is polar."
    >
      <div className="flex flex-wrap gap-2" role="group" aria-label="Choose a molecule or ion">
        {VSEPR_SPECIES_CASES.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-pressed={entry.id === speciesId}
            aria-label={entry.name}
            onClick={() => {
              setSpeciesId(entry.id);
              setAnswers(EMPTY_ANSWERS);
              setResults(null);
            }}
            className={pillClass(entry.id === speciesId)}
          >
            {entry.formula}
          </button>
        ))}
      </div>

      {species.expandedOctet ? (
        <p className="mt-4 flex items-start gap-2 rounded-2xl border border-brand-sunshine/50 bg-wash-sunshine px-4 py-3 text-sm font-bold leading-6 text-ink">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-brand-coral" aria-hidden="true" />
          Beyond some syllabuses: this species uses an expanded octet.
        </p>
      ) : null}

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0 rounded-[1.5rem] border border-brand-sky/25 bg-gradient-to-br from-white to-wash-sky p-4">
          <p className="text-xs font-extrabold uppercase tracking-wide text-slate2">
            {results ? 'Lewis structure' : 'Skeleton: which atoms are joined'}
          </p>
          <MoleculeDiagram
            structure={species.structure}
            variant="lewis"
            skeleton={!results}
            label={
              results
                ? `Lewis structure of ${species.name}`
                : `Skeleton of ${species.name}: connections only, lone pairs and multiple bonds hidden`
            }
          />
          <p className="mt-2 text-center text-xs font-bold leading-5 text-slate2">
            {results
              ? 'Lines are shared pairs; dot pairs are lone pairs. A Lewis structure shows connections, not 3D shape.'
              : 'Lone pairs and double or triple bonds appear after you check.'}
          </p>
          <div className="mt-3 rounded-2xl bg-white/80 px-3 py-2" aria-label="Bond polarity working">
            <p className="text-xs font-extrabold uppercase tracking-wide text-slate2">Bond polarity (Pauling χ)</p>
            {polaritySteps ? (
              <ul className="mt-1 space-y-1">
                {polaritySteps.map((step) => (
                  <li key={step.pair} className="font-mono text-xs font-bold text-ink" data-testid={`bond-polarity-${step.pair}`}>
                    {step.working}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-xs font-bold text-ink-soft">No reviewed electronegativity for one of these atoms.</p>
            )}
            <p className="mt-1 text-xs font-semibold text-slate2">
              A polar bond does not make a polar molecule on its own: the shape decides whether the dipoles cancel.
            </p>
          </div>
        </div>

        <div className="grid min-w-0 gap-4 sm:grid-cols-2">
          <label className="text-sm font-extrabold text-ink">
            {FIELD_LABELS.valenceElectrons}
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_VALENCE_INPUT}
              value={answers.valenceElectrons ?? ''}
              onChange={(event) =>
                update('valenceElectrons', event.target.value === '' ? null : Number(event.target.value))
              }
              className={selectClass}
            />
          </label>
          <label className="text-sm font-extrabold text-ink">
            Domains on {central.element}
            <select
              value={answers.domains ?? ''}
              onChange={(event) => update('domains', event.target.value === '' ? null : Number(event.target.value))}
              className={selectClass}
            >
              <option value="">Choose…</option>
              {DOMAIN_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-extrabold text-ink">
            Lone pairs on {central.element}
            <select
              value={answers.lonePairs ?? ''}
              onChange={(event) => update('lonePairs', event.target.value === '' ? null : Number(event.target.value))}
              className={selectClass}
            >
              <option value="">Choose…</option>
              {LONE_PAIR_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-extrabold text-ink">
            {FIELD_LABELS.shape}
            <select
              value={answers.shape ?? ''}
              onChange={(event) => update('shape', event.target.value === '' ? null : (event.target.value as ShapeName))}
              className={selectClass}
            >
              <option value="">Choose…</option>
              {SHAPE_ORDER.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="sm:col-span-2">
            <legend className="text-sm font-extrabold text-ink">{FIELD_LABELS.polarity}</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {POLARITY_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={answers.polarity === option}
                  onClick={() => update('polarity', option)}
                  className={pillClass(answers.polarity === option)}
                >
                  {option === 'ion' ? 'Ion' : POLARITY_LABELS[option]}
                </button>
              ))}
            </div>
          </fieldset>
          <button
            type="button"
            disabled={!complete}
            onClick={() => setResults(checkVsepr(species, answers))}
            className={`${primaryButtonClass} justify-self-start`}
          >
            Check shape
          </button>
        </div>
      </div>

      {results && model ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)]">
          <Feedback
            correct={results.every((item) => item.correct)}
            label="VSEPR feedback"
            title={`${results.filter((item) => item.correct).length} of ${results.length} correct`}
          >
            <ul className="space-y-1">
              {results.map((item) => (
                <li key={item.field}>
                  <strong className="text-ink">
                    {item.correct ? '✓' : '✗'} {FIELD_LABELS[item.field]}:
                  </strong>{' '}
                  {item.feedback}
                </li>
              ))}
            </ul>
            <p className="pt-2">{species.note}</p>
            {species.octetNote ? <p className="font-bold text-ink">{species.octetNote}</p> : null}
          </Feedback>
          <div className="mt-4 rounded-[1.5rem] border border-hairline bg-white p-4">
            <ShapeSketchView
              shape={model.shape}
              lonePairs={model.lonePairs}
              angleDegrees={species.angleDegrees}
              centralSymbol={central.element}
              terminalSymbols={terminalSymbols}
              angleLabel={species.angleLabel}
            />
            <p className="mt-3 text-center text-sm font-extrabold text-ink">
              {model.shape} · bond angle {species.angleLabel}
            </p>
            <p className="text-center text-xs font-bold text-slate2">VSEPR ideal: {model.idealAngle}</p>
          </div>
        </div>
      ) : null}
    </Panel>
  );
}
